
import { BASE_URL } from "@/config/common";
import { User } from "@/helpers/types/User";
import { ApiResponse } from "@/types/api";

export const getCurrentUser = async (): Promise<ApiResponse<User | null>> => {
  console.log("getCurrentUser");

  try {
    // Step 1: Fetch access token
    // const responseToken = await fetch(`${BASE_URL}/getAuthToken`);
    // const accessToken = (await responseToken.json()).accessToken;

    //console.log("Access token obtained ->", accessToken);

    // Step 2: Attempt to fetch the current user
    const getUserResponse = await fetch(`${BASE_URL}/user/get`, {
      credentials: "include",
    });

    if (getUserResponse.ok) {
      // Successfully fetched the current user
      const data: ApiResponse<User> = await getUserResponse.json();
      return data;
    }

    if (getUserResponse.status === 401) {
      // Access token invalid, attempt to refresh
      console.log("Access token expired, refreshing token...");

      const refreshTokenResponse = await fetch(`${BASE_URL}/refresh_token`, {
        method: "GET",
        credentials: "include", // Ensure HttpOnly cookies are sent
      });

      if (refreshTokenResponse.ok) {
        console.log("Token refreshed successfully. Retrying getCurrentUser...");

        // Get new access token from the refresh response
        const accessToken = (await refreshTokenResponse.json()).accessToken;

        const retryResponse = await fetch(`${BASE_URL}/user/get`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (retryResponse.ok) {
          const retryData: ApiResponse<User> = await retryResponse.json();
          return retryData;
        } else {
          throw new Error("Failed to fetch current user after token refresh");
        }
      } else {
        console.log("Token refresh failed. Creating guest user...");
      }
    }

    // Step 3: If token refresh fails or other errors occur, create a guest user
    const guestUserResponse = await fetch(`${BASE_URL}/user/guest`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!guestUserResponse.ok) {
      throw new Error("Failed to create guest user");
    }

    const guestData: ApiResponse<User> = await guestUserResponse.json();
    console.log("Guest user created successfully:", guestData);

    return guestData;
  } catch (error) {
    console.error("Error fetching current user:", error);

    return {
      success: false,
      code: 500,
      message: "Failed to fetch or create a user",
      data: null,
    };
  }
};

export const refreshToken = async (): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/refresh_token`, {
      method: "GET",
      credentials: "include", // Ensure cookies are sent automatically (HttpOnly cookies)
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    console.log("Refresh token success:", data);
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw new Error("Unable to refresh token");
  }
};

// export const createGuestUserIfNotExists = async (): Promise<
//   ApiResponse<null>
// > => {
//   try {
//     const responseUser = await authenticatedFetch(`${BASE_URL}/user/get`);

//     // If the user exists
//     const userData: ApiResponse<User> = await responseUser.json();
//     console.log("Existing user data retrieved:", userData);

//     return {
//       success: true,
//       code: 200,
//       message: "Existing user fetched successfully",
//       data: null,
//     };
//   } catch (error) {
//     console.error("Error fetching existing user:", error);

//     // If user doesn't exist, create guest user
//     console.log("No existing user found. Creating guest user...");

//     const response = await fetch(`${BASE_URL}/user/guest`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//     });

//     if (!response.ok) {
//       console.error("Failed to create guest user. Status:", response.status);
//       throw new Error(`Failed to create guest user: ${response.statusText}`);
//     }

//     const data: ApiResponse<User> = await response.json();
//     console.log("Guest user created successfully:", data);

//     return {
//       success: true,
//       code: 201,
//       message: "Guest user created successfully",
//       data: null,
//     };
//   }
// };

export interface UpdateUserParams {
  username?: string;
  email?: string;
  gender?: string;
  birthday?: string;
  mobile_number?: string;
  alt_phone?: string;
  profile?: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface SignUpResponse {
  success: boolean;
  message: string;
  user?: {
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export async function updateUserDetails(params: UpdateUserParams): Promise<ApiResponse<User | null>> {
  let res: ApiResponse<User | null> = {
    success: false,
    message: "No response",
    code: 500,
    data: null
  }
  const response = await fetch("/api/user/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params)
  })
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message)
  }
  return result
}

export async function signUp(params: SignUpParams): Promise<SignUpResponse> {
  const response = await fetch("/api/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Something went wrong.");
  }

  return data;
}

export interface LoginParams {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  code: number;
  data?: {
    user: {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
}

export async function login(params: LoginParams): Promise<LoginResponse> {
  const response = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Something went wrong.");
  }

  return data;
}

export async function logout(): Promise<CommonAPIResponse<null>> {
  try {
    const response = await fetch("/api/user/logout", {
      method: "POST",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || "Something went wrong during logout.");
    }

    return data; // Assuming the API returns { success: true, message: "Logged out successfully", etc. }
  } catch (error: any) {
    // This handles any other unexpected errors (network issues, etc.)
    console.error("Logout failed", error);
    throw new Error(error.message || "An error occurred during logout.");
  }
}

export interface GoogleSignInParams {
  email: string;
  google_id: string;
  first_name?: string;
  last_name?: string;
}

export interface GoogleSignInResponse {
  success: boolean;
  message: string;
  code: number;
  data?:{
    user: {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
}

export async function googleSignIn(
  params: GoogleSignInParams
): Promise<GoogleSignInResponse> {
  const response = await fetch("/api/user/google_sign_in", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Something went wrong.");
  }

  return data;
}
